/**
 * 日期处理工具 — 基于 dayjs（2KB，moment.js 兼容 API）
 *
 * 使用示例：
 *   ttx.date.format(new Date(), 'YYYY-MM-DD HH:mm:ss')  // "2026-06-03 14:30:00"
 *   ttx.date.add('2026-06-03', 7, 'day')                  // 7 天后
 *   ttx.date.diff('2026-06-10', '2026-06-03', 'day')      // 7
 *   ttx.date.startOf('month')                              // 本月第一天
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 启用 dayjs 的 utc 与 timezone 插件
// 若插件加载/扩展失败（依赖问题），_tzReady 为 false，后续降级用原生 Date 实现偏移计算
var _tzReady = false;
try {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    _tzReady = typeof dayjs.tz === 'function';
} catch (e) {
    _tzReady = false;
}

window.ttx = window.ttx || {};
ttx.date = ttx.date || {};

(function () {
    /**
     * 将任意日期输入转为 dayjs 对象
     * @param {string|Date|number} date - 日期字符串/Date/时间戳
     * @returns {dayjs}
     */
    function d(date) {
        return dayjs(date);
    }

    /**
     * 格式化日期
     * @param {string|Date|number} date - 日期，默认当前时间
     * @param {string} template - 格式模板，默认 'YYYY-MM-DD HH:mm:ss'
     * @returns {string}
     */
    ttx.date.format = function (date, template) {
        return d(date).format(template || 'YYYY-MM-DD HH:mm:ss');
    };

    /**
     * 日期加减
     * @param {string|Date|number} date - 基准日期，默认当前时间
     * @param {number} value - 加减量
     * @param {string} unit - 单位：year/month/week/day/hour/minute/second
     * @returns {Date} 原生 Date 对象
     */
    ttx.date.add = function (date, value, unit) {
        return d(date).add(value || 0, unit || 'day').toDate();
    };

    /**
     * 日期减法
     * @param {string|Date|number} date - 基准日期，默认当前时间
     * @param {number} value - 减量
     * @param {string} unit - 单位
     * @returns {Date}
     */
    ttx.date.subtract = function (date, value, unit) {
        return d(date).subtract(value || 0, unit || 'day').toDate();
    };

    /**
     * 计算两个日期的差值
     * @param {string|Date|number} date1
     * @param {string|Date|number} date2 - 比较日期
     * @param {string} unit - 单位，默认 'day'
     * @returns {number}
     */
    ttx.date.diff = function (date1, date2, unit) {
        return d(date1).diff(d(date2), unit || 'day');
    };

    /**
     * 获取某时间单位的开始
     * @param {string|Date|number} date - 基准日期，默认当前时间
     * @param {string} unit - year/month/week/day/hour/minute/second
     * @returns {Date}
     */
    ttx.date.startOf = function (date, unit) {
        return d(date).startOf(unit || 'day').toDate();
    };

    /**
     * 获取某时间单位的结束
     * @param {string|Date|number} date - 基准日期，默认当前时间
     * @param {string} unit - year/month/week/day/hour/minute/second
     * @returns {Date}
     */
    ttx.date.endOf = function (date, unit) {
        return d(date).endOf(unit || 'day').toDate();
    };

    /**
     * 判断日期是否在另一个日期之前
     * @param {string|Date|number} date1
     * @param {string|Date|number} date2
     * @param {string} unit - 比较粒度，默认 'day'
     * @returns {boolean}
     */
    ttx.date.isBefore = function (date1, date2, unit) {
        return d(date1).isBefore(d(date2), unit || 'day');
    };

    /**
     * 判断日期是否在另一个日期之后
     * @param {string|Date|number} date1
     * @param {string|Date|number} date2
     * @param {string} unit - 比较粒度，默认 'day'
     * @returns {boolean}
     */
    ttx.date.isAfter = function (date1, date2, unit) {
        return d(date1).isAfter(d(date2), unit || 'day');
    };

    /** 获取当前时间戳（毫秒） */
    ttx.date.now = function () {
        return Date.now();
    };

    /** 获取当前日期字符串 YYYY-MM-DD */
    ttx.date.today = function () {
        return d().format('YYYY-MM-DD');
    };
})();

// ============================================================================
// 以下为 MDF cb.format.transferDatetimeTimezone 兼容 API（时区转换）
// 保留上述已有 API 不变
// 注意：timepicker/datepicker 时区不敏感，仅日期时间格式对时区敏感
// ============================================================================

/**
 * 内部：获取服务端时区
 * 优先从 window.jDiwork.diworkContext() / window.diworkContext() 的 serverTimezone 取
 * @returns {string|null}
 */
function _getServerTimezone() {
    try {
        var ctx = null;
        if (window.jDiwork && typeof window.jDiwork.diworkContext === 'function') {
            ctx = window.jDiwork.diworkContext();
        }
        if (!ctx && typeof window.diworkContext === 'function') {
            ctx = window.diworkContext();
        }
        if (ctx && ctx.serverTimezone) return ctx.serverTimezone;
    } catch (e) { /* 忽略 */ }
    return null;
}

/**
 * 内部（降级用）：原生方式获取指定时区相对 UTC 的偏移量（分钟）
 * 利用 Intl.DateTimeFormat 将同一时刻按指定时区格式化，与 UTC 比较得出偏移。
 * @param {string} timeZone - IANA 时区名，如 'Asia/Shanghai'
 * @param {Date} [date] - 基准时刻，默认当前时间
 * @returns {number} 偏移分钟数（东区为正，如 Shanghai 返回 480）
 */
function _nativeOffsetMinutes(timeZone, date) {
    var dt = date || new Date();
    var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var parts = dtf.formatToParts(dt);
    var map = {};
    for (var i = 0; i < parts.length; i++) {
        map[parts[i].type] = parts[i].value;
    }
    var hour = map.hour === '24' ? 0 : parseInt(map.hour, 10);
    // 把目标时区的墙钟时间当作 UTC 构造时间戳
    var asUTC = Date.UTC(
        parseInt(map.year, 10),
        parseInt(map.month, 10) - 1,
        parseInt(map.day, 10),
        hour,
        parseInt(map.minute, 10),
        parseInt(map.second, 10)
    );
    // 偏移 = 目标时区墙钟对应的 UTC - 真实 UTC
    return Math.round((asUTC - dt.getTime()) / 60000);
}

/**
 * 将服务器时区时间转换为工作台首选项时区
 *
 * @param {object} options
 * @param {string} options.value - 待转换的时间字符串（必填）
 * @param {string} options.format - 时间格式，如 'YYYY-MM-DD HH:mm:ss'（必填）
 * @param {string} [options.timezone] - 工作台首选项时区，默认取 ttx.context.global.getTimeZone()，再不行 'Asia/Shanghai'
 * @param {string} [options.serverTimezone] - 服务端时区，默认取 diworkContext().serverTimezone，再不行 'Asia/Shanghai'
 * @returns {{deviceUTC:object, diworkUTC:object, serverUTC:object, valueUTC:object, value:object}|null}
 *   deviceUTC  - 当前设备 UTC 时间（dayjs 对象）
 *   diworkUTC  - 当前工作台时区时间（dayjs 对象）
 *   serverUTC  - 当前服务端时区时间（dayjs 对象）
 *   valueUTC   - 入参 value 对应的真实 UTC（dayjs 对象）
 *   value      - 调整为工作台时区的 dayjs 对象（展示用）
 */
ttx.date.transferDatetimeTimezone = function (options) {
    options = options || {};
    var value = options.value;
    var format = options.format;
    if (value == null || value === '' || !format) {
        ttx.console.warn('[ttx.date.transferDatetimeTimezone]: value 与 format 为必填参数');
        return null;
    }

    // 工作台首选项时区
    var timezone = options.timezone
        || (ttx.context && ttx.context.global && typeof ttx.context.global.getTimeZone === 'function' ? ttx.context.global.getTimeZone() : null)
        || 'Asia/Shanghai';
    // 服务端时区
    var serverTimezone = options.serverTimezone || _getServerTimezone() || 'Asia/Shanghai';

    var nowMs = Date.now();
    var result = { deviceUTC: null, diworkUTC: null, serverUTC: null, valueUTC: null, value: null };

    if (_tzReady && typeof dayjs.tz === 'function') {
        // 主路径：dayjs timezone 插件
        result.deviceUTC = dayjs.utc(nowMs);
        result.diworkUTC = dayjs.tz(nowMs, timezone);
        result.serverUTC = dayjs.tz(nowMs, serverTimezone);
        // value 按服务端时区墙钟解析，再转 UTC
        var parsed = dayjs.tz(value, format, serverTimezone);
        if (!parsed.isValid()) {
            ttx.console.warn('[ttx.date.transferDatetimeTimezone]: value 解析失败, value=' + value + ', format=' + format);
            return null;
        }
        result.valueUTC = parsed.utc();
        // 转换为工作台时区（展示用）
        result.value = parsed.tz(timezone);
    } else if (typeof dayjs.utc === 'function') {
        // 降级路径1：仅有 utc 插件，用 Intl 计算偏移量
        var tzOffset = _nativeOffsetMinutes(timezone, new Date(nowMs));
        var serverOffset = _nativeOffsetMinutes(serverTimezone, new Date(nowMs));
        result.deviceUTC = dayjs.utc(nowMs);
        // 各时区当前墙钟 = UTC + 偏移（用 dayjs.utc 包装偏移后的时间戳，format 时即为对应时区墙钟）
        result.diworkUTC = dayjs.utc(nowMs + tzOffset * 60000);
        result.serverUTC = dayjs.utc(nowMs + serverOffset * 60000);
        // 解析 value 墙钟（按 UTC 解释，得到"墙钟时间戳"）
        var wallClock = dayjs.utc(value, format);
        if (!wallClock.isValid()) {
            ttx.console.warn('[ttx.date.transferDatetimeTimezone]: value 解析失败, value=' + value + ', format=' + format);
            return null;
        }
        // 服务端时区墙钟对应的真实 UTC = 墙钟时间戳 - 服务端偏移
        var valueUtcMs = wallClock.valueOf() - serverOffset * 60000;
        result.valueUTC = dayjs.utc(valueUtcMs);
        // 工作台时区墙钟 = 真实 UTC + 工作台偏移（dayjs.utc 包装，format 时即为工作台墙钟，仅供展示）
        result.value = dayjs.utc(valueUtcMs + tzOffset * 60000);
    } else {
        // 降级路径2：无任何插件，无法进行时区转换
        ttx.console.warn('[ttx.date.transferDatetimeTimezone]: dayjs utc/timezone 插件不可用，无法转换时区');
        return null;
    }

    return result;
};

/**
 * 获取工作台首选项格式
 * @param {string} type - 格式类型：'dateTimeFormat' | 'dateFormat'
 * @returns {string} 格式模板，取不到时返回默认值
 */
ttx.date.getGlobaFormat = function (type) {
    var dataFormat = (ttx.context && ttx.context.global && typeof ttx.context.global.getDataFormat === 'function')
        ? ttx.context.global.getDataFormat()
        : null;
    if (dataFormat && dataFormat[type]) {
        return dataFormat[type];
    }
    // 兜底默认格式
    return type === 'dateFormat' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss';
};

/**
 * 便捷方法：将服务端时区时间转换为工作台时区并返回格式化字符串
 * 日期数据必须与 format 完全一致。
 * @param {string} value - 时间值
 * @param {string} format - 格式模板
 * @param {string} [timezone] - 工作台时区
 * @param {string} [serverTimezone] - 服务端时区
 * @returns {string} 转换后的格式化字符串（转换失败时原样返回 value）
 */
ttx.date.parseWithTimezone = function (value, format, timezone, serverTimezone) {
    var result = ttx.date.transferDatetimeTimezone({
        value: value,
        format: format,
        timezone: timezone,
        serverTimezone: serverTimezone
    });
    if (!result || !result.value) return value;
    return result.value.format(format);
};
